import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormlyType } from '../ga-formly.service';
import { from, isObservable, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, pairwise, startWith, switchMap, tap } from 'rxjs/operators';
import { FormattingService } from '../../services/formatting.service';
import { util } from '../../util';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../../services/auth/auth.service';
import { ExtraFieldType } from '../extra-field-type';
import { GaFormlyUtil } from '../ga-formly.util';
import * as _ from 'underscore';
import { NgbTypeaheadConfig } from '@ng-bootstrap/ng-bootstrap';

const DEBOUNCE_TIME:number = 300;

@Component({
  selector: 'formly-field-search',
  template: `
    <div class="search-field">
      <!-- in this template formControl.value will only updated when item selected. Custom validators need to access the custom model in component -->
      <ng-container *ngIf="hasValueProp; else regularSearchField">
        <input
          type="text"
          class="form-control"
          [ngClass]="classNames"
          [formlyAttributes]="field"
          [ngModel]="inputModel"
          (ngModelChange)="onInputModelChange($event)"
          [ngbTypeahead]="search"
          [inputFormatter]="formatter"
          [resultFormatter]="formatter"
          [editable]="isEditable()"
          [placeholder]="to.placeholder"
          [inputFocus]="to.inputFocus"
          (selectItem)="onSelectValue($event.item)"
          #inputElement
        >

        <input type="hidden" [formControl]="formControl" />
      </ng-container>
      
      <ng-template #regularSearchField>
        <input
          type="text"
          class="form-control"
          [ngClass]="classNames"
          [formlyAttributes]="field"
          [formControl]="formControl"
          [ngbTypeahead]="search"
          [inputFormatter]="formatter"
          [resultFormatter]="formatter"
          [editable]="isEditable()"
          [placeholder]="to.placeholder"
          [inputFocus]="to.inputFocus"
          #inputElement
        >
      </ng-template>

      <div class="search-field-icon">
        <i class="fa fa-spinner fa-spin activity" *ngIf="loading"></i>
        <i class="fa fa-times clear" *ngIf="!loading && formControl.value" (click)="clear()"></i>
      </div>
    </div>
  `
})
export class FormlyFieldSearchComponent extends ExtraFieldType implements OnInit {
  public loading:boolean;
  public classNames:string;
  public inputModel: any;
  @ViewChild('inputElement') public inputElement:ElementRef<HTMLInputElement>;

  private formatFn:Function;

  constructor(
    private authService:AuthService,
    private formattingService:FormattingService,
    private translateService:TranslateService,
    private http:HttpClient,
    private formlyUtil: GaFormlyUtil,
    config:NgbTypeaheadConfig
  ) {
    super();
    // fix search overflow container
    config.container = 'body';
  }

  protected listen(): void {
    super.listen();

    if (this.hasValueProp) {
      this.formControl.valueChanges
        .pipe(
          startWith<any, any>(this.formControl?.value),
          distinctUntilChanged(_.isEqual),
          pairwise()
        ).subscribe(([ oldValue, newValue ]:[ any, any ]) => {
        if (_.isObject(newValue)) {
          // add timeout here to prevent expression has changed error when it's in detection lifecycle
          setTimeout(() => {
            this.inputModel = newValue;
          });

          this.onSelectValue(newValue);
        } else {
          // if it's remote search and 'valueProp' is defined then should get value from rest API again to update display value
          // loading should be false because first 'fillField' is not counted
          // model value needed for search field should be object but 'formControl' value always string or number when 'valueProp' is defined
          // we need display value in search field should reflected with 'formControl' value
          // eslint-disable-next-line eqeqeq
          if (!this.to.localSearch && this.to.getValueRemotely && (oldValue != newValue) && !this.to.loading) {
            this.to.disabled = true;
            this.to.loading = true;

            if (newValue) {
              this.to.getValueRemotely(newValue)
                .then((result: any) => {
                  this.formControl.setValue(result);
                })
                .finally(() => {
                  this.to.disabled = false;
                  this.to.loading = false;
                });
            } else {
              // fix: when user manually clear search input value then don't load data remotely
              this.formControl.setValue(newValue);
              this.to.disabled = false;
              this.to.loading = false;
            }
          }
        }
      });
    }
  }

  public ngOnInit():void {
    this.to.translateKeyPlaceholder = this.translateService.instant(this.to.translateKeyPlaceholder) === this.to.translateKeyPlaceholder ? 'GA.FORM.SEARCH.PLACEHOLDER' : this.to.translateKeyPlaceholder;
    this.to.placeholder = this.translateService.instant(this.to.placeholder || this.to.translateKeyPlaceholder);
    this.classNames = this.formlyUtil.createClassNames(FormlyType.SEARCH, <string>this.key, this.to.label);
    this.formatFn = this.formlyUtil.getFormatFn(this.to.labelProp || this.to.format);

    // register listener
    this.listen();
  }

  public onInputModelChange(value: any): void {
    if (this.to.onChange) {
      this.to.onChange(value);
    }
  }

  public onSelectValue(obj: any): void {
    if (obj) {
      const valueProp: string = _.isString(this.to.valueProp) ? this.to.valueProp : undefined;
      this.formControl.setValue(valueProp ? obj[valueProp] : obj);
    }
  }

  public get hasValueProp(): boolean {
    return _.isString(this.to?.valueProp);
  }

  public formatter = (obj:any):string => {
    if (this.to.parseHtml) {
      return this.parseHtml(obj);
    } else {
      return this.formatFn(obj);
    }
  };

  public search = (text$:Observable<string>) => {
    if (this.to.searchFunction) {
      return this.serviceSearch(text$);
    } else {
      return (this.to.localSearch || this.to.options)? this.localSearch(text$) : this.remoteSearch(text$);
    }
  };

  private localSearch(text$:Observable<string>):Observable<any> {
    return text$.pipe(
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged(),
      tap(() => this.loading = true),
      map((term:string) => {
        // if min search length is defined then check if the inputted keyword is longer than defined minSearchLength
        if (this.to.minSearchLength && term.length < this.to.minSearchLength) {
          return [];
        }
        return _.filter(this.to.options, (o:any) => {
          let option:string;
          if (this.to.labelProp && this.to.valueProp) {
            option = o[<string>this.to.labelProp];
          } else if (this.formatFn) {
            option = this.formatFn(o);
          } else {
            option = o;
          }

          option = option.toLowerCase();
          const optionWithDiacritics:string = this.formattingService.replaceDiacritics(option);
          // match both: the options with and without diacritics
          return option.indexOf(term.toLowerCase()) !== -1 || optionWithDiacritics.indexOf(term.toLowerCase()) !== -1;
        });
      }),
      tap(() => this.loading = false)
    );
  }

  private remoteSearch(text$:Observable<string>):Observable<any> {
    return text$.pipe(
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged(),
      tap(() => this.loading = true),
      switchMap((term:string) => {
        // if min search length is defined then check if the inputted keyword is longer than defined minSearchLength
        if (this.to.minSearchLength && term.length < this.to.minSearchLength) {
          return of([]);
        }

        // do not search when the field is empty
        if(!term){
          this.clear();
          return of([]);
        }

        const params = _.extend({ filter: term }, this.to.params);

        return this.http.get(this.authService.authenticatedUrl(this.to.url), { params })
          .pipe(
            map((results:any) => _.isArray(results.data)? results.data : results.data.data),
            catchError(() => of([]))
          );
      }),
      tap(() => this.loading = false)
    );
  }

  private serviceSearch(text$:Observable<string>):Observable<any> {
    return text$.pipe(
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged(),
      tap(() => this.loading = true),
      switchMap((term:string) => {
        // if min search length is defined then check if the inputted keyword is longer than defined minSearchLength
        if (this.to.minSearchLength && term.length < this.to.minSearchLength) {
          return of([]);
        }

        // check if its promise then it will be converted to observable
        // ngbTypeahead is expecting only Observable response
        const searchFn:Promise<any> = this.to.searchFunction.request(term);
        if (!isObservable(searchFn)) {
          return from(searchFn)
            .pipe(
              catchError(() => of([]))
            );
        } else {
          return searchFn
            .pipe(
              map((results:any) => _.isArray(results.data)? results.data : results.data.data),
              catchError(() => of([]))
            );
        }
      }),
      tap(() => this.loading = false)
    );
  }

  public isEditable():boolean {
    return util.notNullOrUndefinedOrEmpty(this.to.autoClear) && !this.to.autoClear;
  }

  public clear():void {
    // if form is disabled, prevent user clearing model by clicking x icon
    if (this.to.disabled) {
      return;
    }

    this.inputModel = undefined;
    this.formControl.setValue(undefined);

    // should trigger on change after clear
    if (this.to.onChange) {
      this.to.onChange(this.formControl.value);
    }

    //also clear the input control
    setTimeout(() => {
      this.inputElement.nativeElement.value = '';
      this.inputElement.nativeElement.focus();
    });

  }

  // this formats the selected option by replacing br tags with ',' and removing html tags
  private parseHtml(model:any):string {
    let formattedValue:string = this.formatFn(model);
    formattedValue = this.formattingService.removeHtmlBreaks(formattedValue, ', ');
    return this.formattingService.removeHtmlTags(formattedValue);
  }
}